import exifr from 'exifr';

/**
 * EXIF 데이터 분석 도구
 * 문제가 있는 파일과 정상 파일을 비교 분석
 */
class ExifAnalyzer {
  constructor() {
    this.analysisResults = [];
  }

  /**
   * 단일 파일의 EXIF 데이터 분석
   */
  async analyzeSingleFile(file) {
    try {
      console.log(`=== EXIF 데이터 분석 시작: ${file.name} ===`);

      const exifData = await exifr.parse(file, { xmp: true });

      const processedExifData = {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        lastModified: file.lastModified,

        // 기본 EXIF 정보
        make: exifData.make,
        model: exifData.model,
        dateTime: exifData.dateTime,
        dateTimeOriginal: exifData.dateTimeOriginal,

        // GPS 정보 (exifr은 이미 도 단위로 제공)
        gpsLatitude: exifData.latitude,
        gpsLongitude: exifData.longitude,
        gpsAltitude: exifData.altitude,
        gpsAltitudeRef: exifData.altitudeRef,

        // 이미지 정보
        imageWidth: exifData.imageWidth,
        imageHeight: exifData.imageHeight,
        orientation: exifData.orientation,

        // 촬영 정보
        fNumber: exifData.fNumber,
        exposureTime: exifData.exposureTime,
        iso: exifData.iso,
        focalLength: exifData.focalLength,

        // 썸네일 정보
        thumbnail: exifData.thumbnail,

        // 기타 메타데이터
        software: exifData.software,
        artist: exifData.artist,
        copyright: exifData.copyright,

        // XMP 메타데이터 (DJI M4T 드론)
        gimbalPitchDegree: exifData.GimbalPitchDegree,
        relativeAltitude: exifData.RelativeAltitude,
        flightYawDegree: exifData.FlightYawDegree,

        // 추가 분석 정보
        hasGps: !!(exifData.latitude && exifData.longitude),
        hasThumbnail: !!exifData.thumbnail,
        megapixels: 0
      };

      // 메가픽셀 계산
      if (processedExifData.imageWidth && processedExifData.imageHeight) {
        processedExifData.megapixels =
          (processedExifData.imageWidth * processedExifData.imageHeight) / 1000000;
      }

      // 문제점 분석
      const issues = [];

      if (!processedExifData.gpsLatitude || !processedExifData.gpsLongitude) {
        issues.push('GPS 좌표 정보 누락');
      }

      if (!processedExifData.dateTimeOriginal) {
        issues.push('촬영일시 정보 누락');
      }

      if (!processedExifData.make || !processedExifData.model) {
        issues.push('카메라 정보 누락');
      }

      if (processedExifData.fileSize > 50 * 1024 * 1024) {
        // 50MB 이상
        issues.push(
          `파일 크기가 매우 큼 (${Math.round(processedExifData.fileSize / 1024 / 1024)}MB)`
        );
      }

      if (processedExifData.megapixels > 100) {
        issues.push(`이미지 해상도가 매우 높음 (${processedExifData.megapixels.toFixed(1)}MP)`);
      }

      if (!processedExifData.hasThumbnail) {
        issues.push('썸네일 정보 누락');
      }

      const result = {
        file: file,
        exifData: processedExifData,
        issues: issues,
        analysisTime: new Date().toISOString()
      };

      console.log('=== EXIF 데이터 분석 결과 ===');
      console.log('파일명:', processedExifData.fileName);
      console.log(
        '파일 크기:',
        processedExifData.fileSize,
        'bytes (',
        Math.round(processedExifData.fileSize / 1024 / 1024),
        'MB )'
      );
      console.log('파일 타입:', processedExifData.fileType);
      console.log('제조사:', processedExifData.make);
      console.log('모델:', processedExifData.model);
      console.log('촬영일시:', processedExifData.dateTimeOriginal);
      console.log('GPS 위도:', processedExifData.gpsLatitude);
      console.log('GPS 경도:', processedExifData.gpsLongitude);
      console.log('이미지 크기:', processedExifData.imageWidth, 'x', processedExifData.imageHeight);
      console.log('메가픽셀:', processedExifData.megapixels.toFixed(1), 'MP');
      console.log('조리개값:', processedExifData.fNumber);
      console.log('셔터속도:', processedExifData.exposureTime);
      console.log('ISO:', processedExifData.iso);
      console.log('초점거리:', processedExifData.focalLength);
      console.log('썸네일 존재:', processedExifData.hasThumbnail);
      console.log('소프트웨어:', processedExifData.software);

      if (issues.length > 0) {
        console.log('발견된 문제점들:');
        issues.forEach(issue => console.log('- ' + issue));
      } else {
        console.log('명백한 문제점은 발견되지 않음');
      }

      return result;
    } catch (error) {
      console.error('EXIF 데이터 분석 중 오류 발생:', error);
      throw error;
    }
  }

  /**
   * 여러 파일 비교 분석
   */
  async compareFiles(files, problematicFileName) {
    console.log(`=== 파일 비교 분석 시작 ===`);
    console.log(`문제 파일: ${problematicFileName}`);
    console.log(`비교할 파일 수: ${files.length}`);

    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const result = await this.analyzeSingleFile(file);
        results.push(result);
        console.log(`분석 완료: ${file.name} (${i + 1}/${files.length})`);
      } catch (error) {
        console.error(`파일 분석 실패: ${file.name}`, error);
        results.push({
          file: file,
          exifData: null,
          issues: [`분석 실패: ${error.message}`],
          analysisTime: new Date().toISOString()
        });
      }
    }

    this.performComparisonAnalysis(results, problematicFileName);
    return results;
  }

  /**
   * 비교 분석 수행
   */
  performComparisonAnalysis(results, problematicFileName) {
    console.log(`\n=== 파일 비교 분석 결과 ===`);

    const problematicFile = results.find(r => r.file.name === problematicFileName);
    const normalFiles = results.filter(r => r.file.name !== problematicFileName);

    if (!problematicFile) {
      console.log('❌ 문제 파일을 찾을 수 없습니다.');
      return;
    }

    console.log(`✅ 문제 파일: ${problematicFile.file.name}`);
    console.log(`📊 정상 파일 수: ${normalFiles.length}`);

    // 파일 크기 비교
    this.compareFileSize(problematicFile, normalFiles);

    // 이미지 해상도 비교
    this.compareImageResolution(problematicFile, normalFiles);

    // GPS 정보 비교
    this.compareGpsInfo(problematicFile, normalFiles);

    // 촬영일시 비교
    this.compareDateTime(problematicFile, normalFiles);

    // 카메라 정보 비교
    this.compareCameraInfo(problematicFile, normalFiles);

    // 썸네일 정보 비교
    this.compareThumbnail(problematicFile, normalFiles);

    // 소프트웨어 정보 비교
    this.compareSoftware(problematicFile, normalFiles);

    // 종합 분석
    this.performFinalAnalysis(problematicFile, normalFiles);
  }

  compareFileSize(problematicFile, normalFiles) {
    const problematicSize = problematicFile.exifData?.fileSize || 0;
    const normalSizes = normalFiles.map(f => f.exifData?.fileSize || 0).filter(size => size > 0);

    if (normalSizes.length === 0) return;

    const avgNormalSize = normalSizes.reduce((a, b) => a + b, 0) / normalSizes.length;
    const sizeDiff = problematicSize - avgNormalSize;
    const sizeDiffPercent = (sizeDiff / avgNormalSize) * 100;

    console.log(`\n📏 파일 크기 비교:`);
    console.log(`   문제 파일: ${(problematicSize / 1024 / 1024).toFixed(2)} MB`);
    console.log(`   정상 파일 평균: ${(avgNormalSize / 1024 / 1024).toFixed(2)} MB`);
    console.log(
      `   차이: ${(sizeDiff / 1024 / 1024).toFixed(2)} MB (${sizeDiffPercent.toFixed(1)}%)`
    );

    if (Math.abs(sizeDiffPercent) > 50) {
      console.log(`   ⚠️ 크기 차이가 큽니다!`);
    }
  }

  compareImageResolution(problematicFile, normalFiles) {
    const problematicMp = problematicFile.exifData?.megapixels || 0;
    const normalMps = normalFiles.map(f => f.exifData?.megapixels || 0).filter(mp => mp > 0);

    if (normalMps.length === 0) return;

    const avgNormalMp = normalMps.reduce((a, b) => a + b, 0) / normalMps.length;
    const mpDiff = problematicMp - avgNormalMp;
    const mpDiffPercent = (mpDiff / avgNormalMp) * 100;

    console.log(`\n🖼️ 이미지 해상도 비교:`);
    console.log(`   문제 파일: ${problematicMp.toFixed(1)} MP`);
    console.log(`   정상 파일 평균: ${avgNormalMp.toFixed(1)} MP`);
    console.log(`   차이: ${mpDiff.toFixed(1)} MP (${mpDiffPercent.toFixed(1)}%)`);

    if (Math.abs(mpDiffPercent) > 30) {
      console.log(`   ⚠️ 해상도 차이가 큽니다!`);
    }
  }

  compareGpsInfo(problematicFile, normalFiles) {
    const problematicHasGps = problematicFile.exifData?.hasGps || false;
    const normalHasGpsCount = normalFiles.filter(f => f.exifData?.hasGps).length;
    const normalGpsPercent = (normalHasGpsCount / normalFiles.length) * 100;

    console.log(`\n🌍 GPS 정보 비교:`);
    console.log(`   문제 파일 GPS: ${problematicHasGps ? '✅ 있음' : '❌ 없음'}`);
    console.log(
      `   정상 파일 GPS: ${normalHasGpsCount}/${normalFiles.length} (${normalGpsPercent.toFixed(1)}%)`
    );

    if (!problematicHasGps && normalGpsPercent > 80) {
      console.log(`   ⚠️ 문제 파일에만 GPS 정보가 누락되었습니다!`);
    }
  }

  compareDateTime(problematicFile, normalFiles) {
    const problematicDateTime = problematicFile.exifData?.dateTimeOriginal;
    const normalDateTimes = normalFiles.map(f => f.exifData?.dateTimeOriginal).filter(dt => dt);

    console.log(`\n📅 촬영일시 비교:`);
    console.log(`   문제 파일: ${problematicDateTime || '❌ 없음'}`);
    console.log(`   정상 파일 예시: ${normalDateTimes.slice(0, 3).join(', ')}`);

    if (!problematicDateTime && normalDateTimes.length > normalFiles.length * 0.8) {
      console.log(`   ⚠️ 문제 파일에만 촬영일시가 누락되었습니다!`);
    }
  }

  compareCameraInfo(problematicFile, normalFiles) {
    const problematicMake = problematicFile.exifData?.make;
    const problematicModel = problematicFile.exifData?.model;
    const normalMakes = [...new Set(normalFiles.map(f => f.exifData?.make).filter(make => make))];
    const normalModels = [
      ...new Set(normalFiles.map(f => f.exifData?.model).filter(model => model))
    ];

    console.log(`\n📷 카메라 정보 비교:`);
    console.log(`   문제 파일: ${problematicMake || '❌ 없음'} ${problematicModel || '❌ 없음'}`);
    console.log(`   정상 파일: ${normalMakes.join(', ')} ${normalModels.join(', ')}`);

    if (!problematicMake || !problematicModel) {
      console.log(`   ⚠️ 문제 파일에 카메라 정보가 누락되었습니다!`);
    }
  }

  compareThumbnail(problematicFile, normalFiles) {
    const problematicHasThumbnail = problematicFile.exifData?.hasThumbnail || false;
    const normalHasThumbnailCount = normalFiles.filter(f => f.exifData?.hasThumbnail).length;
    const normalThumbnailPercent = (normalHasThumbnailCount / normalFiles.length) * 100;

    console.log(`\n🖼️ 썸네일 정보 비교:`);
    console.log(`   문제 파일 썸네일: ${problematicHasThumbnail ? '✅ 있음' : '❌ 없음'}`);
    console.log(
      `   정상 파일 썸네일: ${normalHasThumbnailCount}/${normalFiles.length} (${normalThumbnailPercent.toFixed(1)}%)`
    );

    if (!problematicHasThumbnail && normalThumbnailPercent > 80) {
      console.log(`   ⚠️ 문제 파일에만 썸네일이 누락되었습니다!`);
    }
  }

  compareSoftware(problematicFile, normalFiles) {
    const problematicSoftware = problematicFile.exifData?.software;
    const normalSoftwares = [
      ...new Set(normalFiles.map(f => f.exifData?.software).filter(sw => sw))
    ];

    console.log(`\n💻 소프트웨어 정보 비교:`);
    console.log(`   문제 파일: ${problematicSoftware || '❌ 없음'}`);
    console.log(`   정상 파일: ${normalSoftwares.join(', ')}`);
  }

  performFinalAnalysis(problematicFile, normalFiles) {
    console.log(`\n🔍 종합 분석:`);

    const differences = [];

    // 파일 크기 차이
    const problematicSize = problematicFile.exifData?.fileSize || 0;
    const normalSizes = normalFiles.map(f => f.exifData?.fileSize || 0).filter(size => size > 0);
    if (normalSizes.length > 0) {
      const avgNormalSize = normalSizes.reduce((a, b) => a + b, 0) / normalSizes.length;
      const sizeDiffPercent = Math.abs((problematicSize - avgNormalSize) / avgNormalSize) * 100;
      if (sizeDiffPercent > 50) {
        differences.push(`파일 크기가 비정상적 (${sizeDiffPercent.toFixed(1)}% 차이)`);
      }
    }

    // GPS 정보 차이
    const problematicHasGps = problematicFile.exifData?.hasGps || false;
    const normalHasGpsCount = normalFiles.filter(f => f.exifData?.hasGps).length;
    if (!problematicHasGps && normalHasGpsCount > normalFiles.length * 0.8) {
      differences.push('GPS 정보 누락');
    }

    // 촬영일시 차이
    const problematicDateTime = problematicFile.exifData?.dateTimeOriginal;
    const normalDateTimes = normalFiles.map(f => f.exifData?.dateTimeOriginal).filter(dt => dt);
    if (!problematicDateTime && normalDateTimes.length > normalFiles.length * 0.8) {
      differences.push('촬영일시 정보 누락');
    }

    // 썸네일 차이
    const problematicHasThumbnail = problematicFile.exifData?.hasThumbnail || false;
    const normalHasThumbnailCount = normalFiles.filter(f => f.exifData?.hasThumbnail).length;
    if (!problematicHasThumbnail && normalHasThumbnailCount > normalFiles.length * 0.8) {
      differences.push('썸네일 정보 누락');
    }

    // 카메라 정보 차이
    const problematicMake = problematicFile.exifData?.make;
    const problematicModel = problematicFile.exifData?.model;
    if (!problematicMake || !problematicModel) {
      differences.push('카메라 정보 누락');
    }

    if (differences.length > 0) {
      console.log(`   발견된 주요 차이점들:`);
      differences.forEach(diff => console.log(`   - ${diff}`));
    } else {
      console.log(`   명백한 차이점은 발견되지 않음`);
    }

    // 문제 파일의 특별한 이슈들
    if (problematicFile.issues && problematicFile.issues.length > 0) {
      console.log(`   문제 파일의 특별한 이슈들:`);
      problematicFile.issues.forEach(issue => console.log(`   - ${issue}`));
    }

    console.log(`\n=== 분석 완료 ===`);
  }
}

// 전역에서 사용할 수 있도록 export
export default ExifAnalyzer;

// 브라우저 콘솔에서 직접 사용할 수 있도록 window에 등록
if (typeof window !== 'undefined') {
  window.ExifAnalyzer = ExifAnalyzer;
  console.log('🔧 ExifAnalyzer가 전역에서 사용 가능합니다.');
  console.log('사용법: const analyzer = new ExifAnalyzer();');
  console.log('분석: analyzer.analyzeSingleFile(file).then(result => console.log(result));');
  console.log(
    '비교: analyzer.compareFiles(files, "문제파일명.jpg").then(results => console.log(results));'
  );
}
